import { mergeStates } from '../game/merge';
import type { LeaderboardEntry, PlayerState } from '../game/types';
import {
  API,
  type AuthExchangeResponse,
  type CreatePlayerResponse,
  type CreateTripResponse,
  type FriendCodeResponse,
  type FriendsResponse,
  type JoinTripResponse,
  type RestoreResponse,
  type SyncRequest,
  type SyncResponse,
} from '../api/types';
import { activeTrip } from '../game/types';
import { endedOwnedTrips, unregisteredTrips, upgradeLegacyBoards } from '../game/trips';
import type { SyncState } from './types';
import { LOCKS, withLock } from './lock';

/**
 * Opportunistic background sync.
 *
 * Two properties keep this simple. First, every push is a *full snapshot*, so
 * there is no queue to manage - if a push fails, the next one carries everything
 * that was missed. Second, the merge is conflict-free (see `merge.ts`), so a
 * push and a pull can be reconciled without asking the user anything.
 *
 * Sync is debounced and fires on trip end and on visibilitychange, never on
 * every tap. At roughly ten syncs per active user per day, D1's free tier of
 * 100,000 row-writes supports thousands of daily players.
 */

const DEFAULT_DEBOUNCE_MS = 30_000;

/**
 * How many finished trips one push reports, matching MAX_ENDED_TRIPS in
 * functions/api/_trips.ts. Trips are stored newest-first, so an id only drops
 * out of the cap once 25 later trips exist - by which point it has been reported
 * on dozens of syncs and the server has long since closed it.
 */
const MAX_ENDED_TRIPS_PER_SYNC = 25;

export interface SyncClientOptions {
  getState: () => PlayerState;
  /** Called when the server had newer state and a merge produced a new local state. */
  onMerged: (state: PlayerState) => void;
  onStatus: (state: SyncState) => void;
  /** Persist the server-issued player id and recovery code after registration. */
  onRegistered: (playerId: string, recoveryCode: string) => Promise<void>;
  /** Reads the stored recovery code, to detect a registration another tab did. */
  getStoredRecoveryCode: () => Promise<string | null>;
  /** Re-read local state after another tab registered and wrote the real id. */
  onAdoptedExisting: () => Promise<void>;
  /** A locally-created trip is now on the server, under this (possibly new) code. */
  onTripRegistered: (localTripId: string, serverTripId: string, joinCode: string) => void;
  /** Fresh standings arrived on a sync response. */
  onLeaderboard: (tripId: string, leaderboard: LeaderboardEntry[]) => void;
  /**
   * A fresh friends board arrived on a sync response. `playerId` is who pushed,
   * so a board that lands after this device has changed identity can be told
   * apart and dropped.
   */
  onFriends: (playerId: string, board: LeaderboardEntry[]) => void;
  debounceMs?: number;
}

/**
 * `postJson` puts the status at the front of the message it throws, so spotting
 * a throttled push is a prefix check rather than anything structural.
 */
function isRateLimited(message: string): boolean {
  return message.startsWith('429');
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`${response.status} ${response.statusText}${detail ? `: ${detail}` : ''}`);
  }
  return response.json() as Promise<T>;
}

export class SyncClient {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<void> | null = null;
  private registered = false;
  private state: SyncState = { status: 'local-only', lastSyncedAt: null, lastError: null };

  constructor(private readonly options: SyncClientOptions) {}

  get syncState(): SyncState {
    return this.state;
  }

  markRegistered(): void {
    this.registered = true;
  }

  private setStatus(patch: Partial<SyncState>): void {
    this.state = { ...this.state, ...patch };
    this.options.onStatus(this.state);
  }

  /** Debounced push. Safe to call on every state change. */
  schedule(): void {
    if (this.timer) clearTimeout(this.timer);
    this.setStatus({ status: 'pending' });
    this.timer = setTimeout(() => {
      void this.flush();
    }, this.options.debounceMs ?? DEFAULT_DEBOUNCE_MS);
  }

  /** Immediate push - used on trip end and when the tab is backgrounded. */
  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    // Collapse concurrent flushes; the snapshot the running one sends is current.
    if (this.inFlight) return this.inFlight;

    this.inFlight = this.push().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async push(): Promise<void> {
    this.setStatus({ status: 'syncing' });

    try {
      if (!this.registered) await this.register();

      // A trip started with no signal only becomes joinable once the server
      // knows about it, so catch up any that are still local-only.
      //
      // Ordering note: this runs before the push, so a player who ends one trip
      // and starts another between syncs can have the new trip handed the old
      // one's join code, with the old one released moments later by the push.
      // Harmless - the server only ever hands out a code that is free at the
      // time - but worth stating so nobody "fixes" the order later.
      await this.registerPendingTrips();

      const local = this.options.getState();
      const trip = activeTrip(local);

      // Every finished trip this player owns goes up on every sync, not just the
      // ones that ended since the last push. That is deliberate: it is a handful
      // of ids on a payload that already carries every trip in full, the server's
      // statement is a no-op once ended_at is set, and a push lost in a blackspot
      // therefore heals itself on the next one - with no "already reported" flag
      // to store, so no SCHEMA_VERSION bump and no IndexedDB migration.
      const ended = endedOwnedTrips(local).slice(0, MAX_ENDED_TRIPS_PER_SYNC);

      const request: SyncRequest = {
        playerId: local.playerId,
        version: local.version,
        payload: local,
        ...(ended.length > 0 ? { endedTrips: ended.map((t) => t.id) } : {}),
        ...(trip?.registered
          ? {
              activeTrip: {
                tripId: trip.id,
                claims: trip.myClaims,
                version: trip.version,
              },
            }
          : {}),
      };

      const result = await postJson<SyncResponse>(API.sync, request);

      if (result.leaderboard && trip) {
        this.options.onLeaderboard(trip.id, result.leaderboard);
      }

      if (result.friends) {
        this.options.onFriends(local.playerId, result.friends);
      }

      if (!result.applied && result.state) {
        // The server held something newer. Merging is conflict-free, so this
        // never loses a claim from either side. The server's copy may predate
        // public ids, so its trip boards are upgraded before they can win.
        const remote = await upgradeLegacyBoards(result.state);
        this.options.onMerged(mergeStates(local, remote));
      }

      this.setStatus({ status: 'synced', lastSyncedAt: Date.now(), lastError: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      // A failed sync must never affect play, so this is only ever a status.
      //
      // Being throttled is back-pressure rather than a fault, and the Settings
      // screen shows `lastError` to the player verbatim, where a raw HTTP line
      // reads as something broken. The next push retries as normal.
      this.setStatus({
        status: navigator.onLine ? 'error' : 'offline',
        lastError: isRateLimited(message)
          ? 'Saving is rate limited, this will retry shortly'
          : message,
      });
    }
  }

  /**
   * Mints a server player, exactly once per origin.
   *
   * The lock, and the re-check inside it, are what stop two tabs opened together
   * on a first visit from creating two players - which would strand one of them
   * and could leave the stored recovery code pointing at the empty save.
   */
  private async register(): Promise<void> {
    await withLock(LOCKS.register, async () => {
      if (this.registered) return;

      // Another tab may have registered while this one waited for the lock.
      const existing = await this.options.getStoredRecoveryCode();
      if (existing) {
        await this.options.onAdoptedExisting();
        this.registered = true;
        return;
      }

      const local = this.options.getState();
      const result = await postJson<CreatePlayerResponse>(API.player, {
        displayName: local.displayName,
      });

      // Store the code before the id: if this tab dies in between, the next load
      // finds the code and adopts the player rather than registering again.
      await this.options.onRegistered(result.playerId, result.recoveryCode);
      this.registered = true;
    });
  }

  /**
   * Registers trips created while offline.
   *
   * A failure here must not block the sync itself - the lifetime collection is
   * the more important of the two ledgers, and the trip will be retried on the
   * next push.
   */
  private async registerPendingTrips(): Promise<void> {
    for (const trip of unregisteredTrips(this.options.getState())) {
      try {
        const result = await postJson<CreateTripResponse>(API.trips, {
          playerId: this.options.getState().playerId,
          name: trip.name,
          joinCode: trip.joinCode,
        });
        this.options.onTripRegistered(trip.id, result.tripId, result.joinCode);
      } catch {
        // Stays local-only; the next sync tries again.
      }
    }
  }

  /** Joins someone else's running trip by its code. */
  async joinTrip(joinCode: string): Promise<JoinTripResponse> {
    const local = this.options.getState();
    return postJson<JoinTripResponse>(API.joinTrip, {
      playerId: local.playerId,
      displayName: local.displayName,
      joinCode,
    });
  }

  /**
   * Registers this device with the server if it has not been already, without
   * pushing a sync. For calls that need a server-side player to exist, where a
   * device that has never had signal would otherwise be told it is unknown.
   * Throws when offline, like any other call here.
   */
  async ensureRegistered(): Promise<void> {
    if (!this.registered) await this.register();
  }

  /** This player's friend code, minted by the server on first request. */
  async getFriendCode(): Promise<FriendCodeResponse> {
    return postJson<FriendCodeResponse>(API.friendCode, {
      playerId: this.options.getState().playerId,
    });
  }

  /** Adds a friend by their code - both ways - and returns the new board. */
  async addFriend(friendCode: string): Promise<FriendsResponse> {
    return postJson<FriendsResponse>(API.addFriend, {
      playerId: this.options.getState().playerId,
      friendCode,
    });
  }

  /** Ends a friendship for both players and returns the new board. */
  async removeFriend(publicId: string): Promise<FriendsResponse> {
    return postJson<FriendsResponse>(API.removeFriend, {
      playerId: this.options.getState().playerId,
      publicId,
    });
  }

  /** Pulls a player's state back using their recovery code. */
  async restore(recoveryCode: string): Promise<RestoreResponse> {
    return postJson<RestoreResponse>(API.restore, { recoveryCode });
  }

  /**
   * Redeems the one-time ticket a completed sign-in left in the URL fragment.
   *
   * Deliberately dumb: it reports what the server decided and changes nothing
   * here. Whether this device links, merges or switches is GameProvider's call,
   * because only it owns identity.
   */
  async exchangeAuthTicket(ticket: string): Promise<AuthExchangeResponse> {
    return postJson<AuthExchangeResponse>(API.authExchange, { ticket });
  }

  async unlinkAuth(playerId: string): Promise<void> {
    await postJson<{ ok: true }>(API.authUnlink, { playerId });
  }
}

/**
 * Wires the flush triggers that matter on a phone: backgrounding the tab (the
 * most reliable "user is done for now" signal on mobile) and regaining
 * connectivity after a blackspot.
 */
export function attachSyncTriggers(client: SyncClient): () => void {
  const onHidden = () => {
    if (document.visibilityState === 'hidden') void client.flush();
  };
  const onOnline = () => void client.flush();

  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('online', onOnline);

  return () => {
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('online', onOnline);
  };
}
